import { useState, useLayoutEffect } from "react";
import { Sparkles, X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "df_promo_dismissed_v1";
const BAR_HEIGHT_PX = 44;

/**
 * Bannière d'annonce en tête de page (fixed top-0, z > header).
 * Elle expose sa hauteur via la variable CSS --df-announce-h afin que le Header
 * (fixed top-0) puisse s'aligner juste en dessous.
 */
export default function AnnouncementBar() {
  // Lu de façon synchrone + useLayoutEffect : le header se place au bon endroit dès le premier paint (zéro CLS)
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem(STORAGE_KEY); } catch { return true; }
  });

  useLayoutEffect(() => {
    document.documentElement.style.setProperty("--df-announce-h", visible ? `${BAR_HEIGHT_PX}px` : "0px");
    return () => {
      document.documentElement.style.setProperty("--df-announce-h", "0px");
    };
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    document.documentElement.style.setProperty("--df-announce-h", "0px");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="announcement-bar"
      role="banner"
      className="fixed top-0 left-0 right-0 z-[60] w-full text-white text-[13px] sm:text-sm font-medium overflow-hidden"
      style={{
        height: BAR_HEIGHT_PX,
        background:
          "linear-gradient(90deg, #007AFF 0%, #38BDF8 50%, #4D8DFF 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-40 announce-sheen" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-3 px-4 h-full">
        <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/25">
          <Sparkles size={12} />
        </span>

        <span className="flex-1 sm:flex-none text-center">
          <strong className="font-bold">Offre limitée</strong>
          <span className="mx-1.5 opacity-70">•</span>
          <span>Testez DriveFlow gratuitement pendant </span>
          <strong className="font-bold underline decoration-white/40 underline-offset-2">1 mois</strong>
          <span className="hidden md:inline"> — sans carte bancaire</span>
        </span>

        <a
          href="#tarifs"
          data-testid="announcement-cta"
          className="hidden sm:inline-flex items-center gap-1 font-semibold bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1"
        >
          J'en profite
          <ArrowRight size={13} />
        </a>

        <button
          type="button"
          onClick={dismiss}
          data-testid="announcement-close"
          aria-label="Fermer l'annonce"
          className="ml-1 sm:ml-2 p-1 rounded-full hover:bg-white/15 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      <style>{`
        @keyframes announceSheen {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .announce-sheen {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.25) 45%,
            rgba(255,255,255,0.45) 50%,
            rgba(255,255,255,0.25) 55%,
            transparent 100%
          );
          animation: announceSheen 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
